import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./ReissuePaywall.module.scss"

/**
 * ReissuePaywall (`/reissue`) — an original, self-contained EXPIRATION / renewal
 * paywall for Private Photo Vault Premium. Paywall type rolled at random this
 * run: EXPIRATION / renewal.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run is `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority, and the whole concept is built to it: privacy told as an
 * ACCESS CREDENTIAL — who your vault's guards answer to.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "Re-issue the key that opens only for you." A subscription is, at heart, a
 * credential that expires and is re-issued — exactly how a secure facility's
 * access card works. Here Premium is the CREDENTIAL that keeps your vault's
 * extra guards on watch. When it lapses the credential reads EXPIRED and the
 * guards stand down — but nothing moves: every photo stays put, always locked
 * behind your PIN. Renewing RE-ENCODES the credential, the reader flips from a
 * red "expired" to a green "only you," and the guards come back on watch.
 * Renewal framed as re-provisioning endowed access — commitment + risk-reversal,
 * calm and exact, deliberately NOT the cold-fear / countdown idiom.
 *
 * ── Interaction (the hero mechanic) ─────────────────────────────────────────
 * A matte credential card is docked at the top of the vault's reader strut. You
 * FEED IT DOWN THROUGH THE FIXED READER: as the card's magnetic stripe passes
 * the write-head it is ENCODED green cell-by-cell, a serial + "VALID THROUGH"
 * prints, and at the bottom it seats into the cradle — the reader eye flips
 * red→green, the four real guards ignite one-by-one, and the wall of your
 * memories brightens from a dimmed "expired" state to lit "on watch." An
 * ENCODE-THROUGH-A-FIXED-READER gesture — new to the set: the card is written by
 * a fixed head as it travels (payoff is the ENCODING + reader state), not a
 * blind draw (Shade), an ink roller (Watermark), a pattern trace (Trace), a
 * dial/crank (Tumbler/Vault Door/Mainspring), a hold-to-converge (Ward) or a
 * tap. Replayable; auto-demos the encode once, a tap re-issues, dragging feeds
 * the card by hand.
 *
 * ── Divergence (vs. the 140+ prior concepts, incl. the security cluster) ────
 * Layout      : a VAULT-DOOR MODULE — the user's memory wall behind a lit door,
 *               with a vertical reader strut on the lock edge that a credential
 *               card feeds down. Not a hero-on-top / bullets / CTA stack, not a
 *               console, certificate, keypad, dial or scene.
 * Interaction : ENCODE-THE-KEY — feed a card down a fixed reader; its stripe is
 *               written as it passes the head. New gesture for the set.
 * Metaphor    : an ACCESS CREDENTIAL re-issued — the most renewal-native
 *               security object there is; unused across the set.
 * Palette     : machined GRAPHITE / GUNMETAL with a single VERIFIED-GREEN go-LED
 *               (red = expired) — fresh; the set is otherwise locked to
 *               blue + amber.
 * Register    : crisp secure-facility competence — "the system recognizes you."
 *               Calm-authority, not fear, not nostalgia.
 * Value frame : re-provisioning endowed access (commitment + risk-reversal +
 *               continuity), contrast red-expired → green-only-you.
 * CTA         : "Re-issue my access" — yearly featured.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every safeguard named is a REAL Private Photo Vault feature (Face ID / pattern
 * lock · encrypted Cloud Vault · break-in reports · app disguise). No countdown,
 * no fabricated scarcity, NO data-loss threat — the credential governs only who
 * the extra GUARDS answer to; your photos never move and stay locked behind your
 * PIN whether Premium is active or not. "Save 79%" is exact arithmetic from live
 * product data. Yearly is the featured, default, most prominent price; monthly
 * is small and secondary. The 7-day free trial is the real trial. Renders on
 * built-in debug product data with bundled photos — no native injection, no
 * external egress.
 */

// The memories behind the vault door — bundled, self-contained repo photos.
const MEMORIES = [
  "/byheart/m02.jpg",
  "/byheart/m14.jpg",
  "/byheart/m05.jpg",
  "/byheart/m11.jpg",
  "/byheart/m09.jpg",
  "/byheart/m08.jpg",
]

// Real Private Photo Vault guards that come back on watch as the card encodes.
// Each carries the encode fraction at which it re-arms (top of strip → seated).
const GUARDS = [
  {
    key: "lock",
    at: 0.18,
    Icon: svg.featureIcons.PatternLock,
    name: "Face ID & pattern lock",
    line: "Answers only to you",
  },
  {
    key: "cloud",
    at: 0.42,
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "A private backup on every device",
  },
  {
    key: "breakin",
    at: 0.66,
    Icon: svg.featureIcons.Breakin,
    name: "Break-in reports",
    line: "A silent photo of anyone who tries",
  },
  {
    key: "disguise",
    at: 0.88,
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "Hide the whole vault behind a plain app",
  },
]

const AUTH_AT = 0.62 // encode fraction past which the credential reads VALID

const monthLabel = (d: Date) =>
  [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ][d.getMonth()]

const ReissuePaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  // The credential's new expiry: one year out, honest to the featured yearly.
  const validThrough = useMemo(() => {
    const now = new Date()
    const then = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate())
    return `${monthLabel(then)} ${then.getFullYear()}`
  }, [])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── encode state ───────────────────────────────────────────────────────────
  // e : 0 = card docked & blank (credential EXPIRED), 1 = fully fed through the
  //     reader & seated (credential VALID). A spring chases the target once the
  //     user lets go; while dragging, e tracks the pointer directly.
  const [e, setE] = useState(0)
  const eRef = useRef(0)
  const targetRef = useRef(0)
  const raf = useRef<number | null>(null)
  const userActed = useRef(false)
  const dragging = useRef(false)
  const strutRef = useRef<HTMLDivElement | null>(null)

  const valid = e > AUTH_AT

  const tick = useCallback(() => {
    const target = targetRef.current
    const cur = eRef.current
    const k = target > cur ? 0.15 : 0.2
    let next = cur + (target - cur) * k
    if (Math.abs(target - next) < 0.0012) next = target
    eRef.current = next
    setE(next)
    if (next !== target) raf.current = requestAnimationFrame(tick)
    else raf.current = null
  }, [])

  const springTo = useCallback(
    (v: number) => {
      targetRef.current = v
      if (reduceMotion) {
        if (raf.current != null) cancelAnimationFrame(raf.current)
        raf.current = null
        eRef.current = v
        setE(v)
        return
      }
      if (raf.current == null) raf.current = requestAnimationFrame(tick)
    },
    [reduceMotion, tick],
  )

  const setImmediate = useCallback((v: number) => {
    if (raf.current != null) {
      cancelAnimationFrame(raf.current)
      raf.current = null
    }
    eRef.current = v
    targetRef.current = v
    setE(v)
  }, [])

  // ── pointer: feed the card down the reader (or draw it back up) ─────────────
  const yToE = useCallback((clientY: number) => {
    const el = strutRef.current
    if (!el) return 0
    const r = el.getBoundingClientRect()
    const raw = (clientY - r.top) / r.height
    return Math.max(0, Math.min(1, raw))
  }, [])

  const onDown = useCallback(
    (ev: React.PointerEvent) => {
      userActed.current = true
      dragging.current = true
      setImmediate(yToE(ev.clientY))
      try {
        ;(ev.currentTarget as Element).setPointerCapture?.(ev.pointerId)
      } catch {}
    },
    [setImmediate, yToE],
  )

  const onMove = useCallback(
    (ev: React.PointerEvent) => {
      if (!dragging.current) return
      setImmediate(yToE(ev.clientY))
    },
    [setImmediate, yToE],
  )

  const onUp = useCallback(() => {
    if (!dragging.current) return
    dragging.current = false
    springTo(eRef.current >= AUTH_AT ? 1 : 0)
  }, [springTo])

  // A tap (no real drag) toggles: re-issue an expired card, or eject a valid one.
  const toggle = useCallback(() => {
    userActed.current = true
    springTo(eRef.current > AUTH_AT ? 0 : 1)
  }, [springTo])

  // Auto-demo once so the payoff reads; reduced-motion arrives valid & calm.
  useEffect(() => {
    if (reduceMotion) {
      setImmediate(1)
      return
    }
    if (window.location.search.includes("nodemo")) return
    const t = window.setTimeout(() => {
      if (!userActed.current) springTo(1)
    }, 950)
    return () => {
      window.clearTimeout(t)
      if (raf.current != null) cancelAnimationFrame(raf.current)
      raf.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const litCount = GUARDS.filter((g) => e >= g.at).length

  return (
    <div
      className={`${styles.page} ${valid ? styles.isValid : ""}`}
      style={{ ["--e" as any]: e }}
    >
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="15" height="15">
                <rect
                  x="4"
                  y="9.5"
                  width="16"
                  height="11"
                  rx="2.2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
                <path
                  d="M7.5 9.5V7a4.5 4.5 0 0 1 9 0v2.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={`${styles.kicker} ${valid ? styles.kickerOn : ""}`}>
            <span className={styles.kickerDot} aria-hidden="true" />
            {valid ? "Access re-issued" : "Access expired"}
          </span>
          <h1 className={styles.title}>
            {valid ? (
              <>
                Re-issued. Your vault
                <br />
                <span className={styles.accent}>opens only for you.</span>
              </>
            ) : (
              <>
                Your key to the vault
                <br />
                <span className={styles.accentWarn}>has expired.</span>
              </>
            )}
          </h1>
          <p className={styles.sub}>
            {valid
              ? "The guards are back on watch, on the credential that answers to you alone. Every photo stayed exactly where it was."
              : "Re-issue the credential and your vault's guards come back on watch. Your photos never moved — they’re always locked behind your PIN."}
          </p>
        </section>

        {/* ── the vault door + reader (hero) ─────────────────────────────── */}
        <section className={styles.stage}>
          <div
            className={`${styles.readout} ${valid ? styles.readoutSafe : ""}`}
            aria-live="polite"
          >
            <span className={styles.readoutDot} aria-hidden="true" />
            {valid ? (
              <>
                Credential · <b>valid, only you</b>
              </>
            ) : (
              <>
                Credential · <b>expired</b>
              </>
            )}
          </div>

          <div className={styles.vault}>
            {/* the wall of your memories behind the door */}
            <div className={styles.wall} aria-hidden="true">
              {MEMORIES.map((src) => (
                <span
                  key={src}
                  className={styles.mem}
                  style={{ backgroundImage: `url(${src})` }}
                />
              ))}
              <span className={styles.wallVeil} />
              <span className={styles.wallSeam} />
            </div>

            {/* the reader strut on the lock edge — the card feeds down here */}
            <div
              ref={strutRef}
              className={styles.strut}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
              role="button"
              tabIndex={0}
              aria-label={
                valid
                  ? "Credential valid — vault opens only for you. Activate to eject the card."
                  : "Feed the credential card down the reader to re-issue your access."
              }
              onKeyDown={(ev) => {
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.preventDefault()
                  toggle()
                }
              }}
            >
              {/* the fixed reader eye / write-head */}
              <span
                className={`${styles.reader} ${valid ? styles.readerOn : ""}`}
                aria-hidden="true"
              >
                <span className={styles.readerLamp} />
                <span className={styles.readerSlot} />
                <span className={styles.readerLabel}>
                  {valid ? "VALID" : "READ"}
                </span>
              </span>

              {/* the credential card being fed down through the reader */}
              <span className={styles.cardTravel} aria-hidden="true">
                <span
                  className={`${styles.card} ${valid ? styles.cardOn : ""}`}
                >
                  <span className={styles.cardChip} />
                  <span className={styles.cardStripe}>
                    <span className={styles.cardStripeWrite} />
                  </span>
                  <span className={styles.cardGlyph}>
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <rect
                        x="5"
                        y="10.5"
                        width="14"
                        height="9.5"
                        rx="1.8"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                      />
                      <path
                        d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                  <span className={styles.cardTick}>
                    <svg viewBox="0 0 24 24" width="10" height="10">
                      <path
                        d="M5 12.5l4.2 4.2L19 7"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </span>
              </span>

              {/* the cradle the card seats into */}
              <span className={styles.cradle} aria-hidden="true" />

              {/* hint chip */}
              <span
                className={`${styles.hint} ${valid ? styles.hintDone : ""}`}
                aria-hidden="true"
              >
                <span className={styles.hintArrow}>
                  <svg viewBox="0 0 24 24" width="12" height="12">
                    <path
                      d="M12 5v13M6 12l6 6 6-6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                {valid ? "Re-issued" : "Feed card"}
              </span>
            </div>
          </div>

          <div
            className={`${styles.validCaption} ${
              valid ? styles.validCaptionOn : ""
            }`}
          >
            {valid ? (
              <>
                Credential re-issued · <b>valid through {validThrough}</b>
              </>
            ) : (
              <>Credential expired · feed the card to re-issue</>
            )}
          </div>

          {/* the four real guards, coming back on watch as the card encodes */}
          <div className={styles.guards}>
            <div className={styles.guardsHead}>
              <span>Back on watch</span>
              <span className={styles.guardsCount}>
                {litCount}/{GUARDS.length} on
              </span>
            </div>
            <div className={styles.guardRow}>
              {GUARDS.map((g) => {
                const on = e >= g.at
                return (
                  <div
                    key={g.key}
                    className={`${styles.guard} ${on ? styles.guardOn : ""}`}
                  >
                    <span className={styles.guardIcon} aria-hidden="true">
                      <g.Icon width={17} height={17} />
                    </span>
                    <span className={styles.guardName}>{g.name}</span>
                    <span className={styles.guardLine}>{g.line}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* ── honest framing ─────────────────────────────────────────────── */}
        <p className={styles.proof}>
          <span className={styles.proofMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <circle
                cx="12"
                cy="12"
                r="8.2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
              />
              <path
                d="M8.5 12.4l2.4 2.4 4.6-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          The credential only decides who the guards answer to. Every photo
          stays put and locked behind your PIN — nothing is moved or deleted.
        </p>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plansLabel}>Re-issue your access</div>

        <button
          className={`${styles.yearly} ${
            plan === "yearly" ? styles.selected : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>
              A full year of Premium
              <span className={styles.trialPill}>
                {trialDays}-day free trial
              </span>
            </span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyFoot}>
            Every guard re-issued for a full year
          </span>
        </button>

        <button
          className={`${styles.monthly} ${
            plan === "monthly" ? styles.monthlySel : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span>Or re-issue monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Re-issue my access — ${trialDays} days free`
              : "Re-issue my access"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default ReissuePaywall
